import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import { Sidebar } from "@/components/Sidebar";
import { PageHeader } from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Facebook, Instagram, Loader2, Share2, ImageIcon, Calendar, Clock, CheckCircle, XCircle, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import Chatbot from "@/components/Chatbot";
import CallingBot from "@/components/Callingbot";

interface Campaign {
  id: string;
  property_id?: string;
  campaign_name?: string;
  property_title?: string;
  campaign_type?: string;
  service_type?: string;
  status: string;
  scheduled_date?: string;
  date?: string;
  image_url?: string;
  thumbnail_url?: string;
  recipients_count?: number;
  html?: string;
  campaign_subject?: string;
  message?: string;
  template?: string;
  custom_template?: any;
  campaign_image?: string;
  created?: string;
  facebook_posted_at?: string | null;
  instagram_posted_at?: string | null;
}

type SocialPlatform = "facebook" | "instagram";

interface PendingPage {
  id: string;
  name: string;
  category?: string | null;
  picture?: string | null;
  igUsername?: string | null;
}

interface PendingSelection {
  platform: SocialPlatform;
  selectionId: string;
  pages: PendingPage[];
}

const POSTED_AT_FIELD = {
  facebook: "facebook_posted_at",
  instagram: "instagram_posted_at",
} as const;

// Instagram processes an uploaded image asynchronously. The backend answers 202
// (+ creation_id) while it is still working; the client polls a short endpoint
// instead of the server holding a worker open.
const INSTAGRAM_POLL_INTERVAL_MS = 2000;
const INSTAGRAM_POLL_TIMEOUT_MS = 90_000;

const sleep = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

// Newest launch first: by creation time, falling back to launch/schedule dates.
const getLaunchSortTime = (c: Campaign) =>
  new Date(c.created || c.date || c.scheduled_date || 0).getTime();

const SocialMediaUploads = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [facebookConnected, setFacebookConnected] = useState(false);
  const [instagramConnected, setInstagramConnected] = useState(false);
  const [loadingConnections, setLoadingConnections] = useState(true);

  const [uploadDialog, setUploadDialog] = useState<{
    isOpen: boolean;
    platform: 'facebook' | 'instagram' | null;
    campaign: Campaign | null;
  }>({ isOpen: false, platform: null, campaign: null });

  const [caption, setCaption] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const [previewImageFailed, setPreviewImageFailed] = useState(false);

  const [facebookPageName, setFacebookPageName] = useState<string | null>(null);
  const [instagramPageName, setInstagramPageName] = useState<string | null>(null);
  const [pendingSelection, setPendingSelection] = useState<PendingSelection | null>(null);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [savingPage, setSavingPage] = useState(false);
  const [connectingInstagram, setConnectingInstagram] = useState(false);
  const connectPollRef = useRef<number | undefined>(undefined);
  // Set when the user leaves the page or closes the dialog: stops Instagram status
  // polling so an abandoned post is not published in the background.
  const publishPollCancelledRef = useRef(false);
  // Lets the header's hamburger open the sidebar drawer on tablets/phones.
  const toggleMobileRef = useRef<(() => void) | null>(null);
  const connectToastKeyRef = useRef<string | null>(null);
  const facebookConnectedRef = useRef(false);
  const instagramConnectedRef = useRef(false);
  const previewGenTokenRef = useRef(0);

  const SOCIAL_CONNECT_MESSAGE_SOURCE = "mylistiq-social-connect";
  const API_ORIGIN = new URL(endpoints.listCampaigns).origin;

  const getStoredAuthToken = () =>
    sessionStorage.getItem("access_token") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    "";

  const clearConnectPoll = () => {
    if (connectPollRef.current !== undefined) {
      window.clearInterval(connectPollRef.current);
      connectPollRef.current = undefined;
    }
  };

  const notifyConnected = (platform: string, name?: string) => {
    const key = `ok:${platform}:${name || ""}`;
    if (connectToastKeyRef.current === key) return;
    connectToastKeyRef.current = key;
    const label = name || `${platform.charAt(0).toUpperCase()}${platform.slice(1)}`;
    toast.success(`Connected to ${label}!`);
  };

  const notifyConnectFailed = (message: string) => {
    const key = `err:${message}`;
    if (connectToastKeyRef.current === key) return;
    connectToastKeyRef.current = key;
    toast.error(message);
  };

  const checkSocialConnections = async (options?: { showLoading?: boolean }) => {
    const showLoading = options?.showLoading === true;

    if (!getStoredAuthToken()) {
      facebookConnectedRef.current = false;
      instagramConnectedRef.current = false;
      setFacebookConnected(false);
      setInstagramConnected(false);
      setLoadingConnections(false);
      return { facebook: false, instagram: false, facebookName: null as string | null, instagramName: null as string | null };
    }

    try {
      if (showLoading) setLoadingConnections(true);
      const response = await axiosInstance.get(endpoints.social_status, {
        params: { _t: Date.now() }, // defeat any intermediary cache
        headers: { "Cache-Control": "no-cache" },
      });
      const facebook = !!response.data.facebook?.isConnected;
      const instagram = !!response.data.instagram?.isConnected;
      facebookConnectedRef.current = facebook;
      instagramConnectedRef.current = instagram;
      setFacebookConnected(facebook);
      setInstagramConnected(instagram);
      setFacebookPageName(response.data.facebook?.pageName ?? null);
      setInstagramPageName(response.data.instagram?.pageName ?? null);

      // The user has several Pages: let them choose which one to connect.
      const pending = response.data.pendingSelection as PendingSelection | null;
      if (pending?.pages?.length) {
        clearConnectPoll();
        connectToastKeyRef.current = "pending-selection";
        setSelectedPageId((prev) =>
          prev && pending.pages.some((p) => p.id === prev) ? prev : pending.pages[0].id
        );
        setPendingSelection(pending);
      }

      // The OAuth popup's postMessage can be lost (Facebook's COOP header severs
      // window.opener), so the backend also reports why the last connect failed.
      const lastError = response.data.lastError;
      if (lastError && typeof lastError.message === "string" && lastError.message) {
        clearConnectPoll();
        notifyConnectFailed(lastError.message);
      }
      const lastResult = response.data.lastResult;
      if (
        lastResult &&
        (lastResult.platform === "facebook" || lastResult.platform === "instagram")
      ) {
        clearConnectPoll();
        notifyConnected(lastResult.platform, lastResult.name || undefined);
      }

      return {
        facebook,
        instagram,
        facebookName: (response.data.facebook?.pageName ?? null) as string | null,
        instagramName: (response.data.instagram?.pageName ?? null) as string | null,
      };
    } catch (err) {
      console.error("Failed to check social connections", err);
      facebookConnectedRef.current = false;
      instagramConnectedRef.current = false;
      setFacebookConnected(false);
      setInstagramConnected(false);
      return { facebook: false, instagram: false, facebookName: null as string | null, instagramName: null as string | null };
    } finally {
      if (showLoading) setLoadingConnections(false);
    }
  };

  useEffect(() => {
    if (!getStoredAuthToken()) {
      setLoading(false);
      setLoadingConnections(false);
      return;
    }

    void fetchSentAndScheduledCampaigns();
    void checkSocialConnections({ showLoading: true });

    // Legacy fallback: older backends hard-redirected with ?connected=
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("connected");
    const pageName = params.get("page_name");

    if (connected === "facebook" || connected === "instagram") {
      const name = pageName ? decodeURIComponent(pageName) : undefined;
      notifyConnected(connected, name);
      void checkSocialConnections();
      window.history.replaceState({}, "", "/social-media");
    }

    const handlePostMessage = (e: MessageEvent) => {
      const data = e.data;
      // Ignore Vite/HMR/extension/analytics traffic — a loose `error` check was
      // re-fetching social status in a loop and making the page/modal flicker.
      if (!data || typeof data !== "object" || data.source !== SOCIAL_CONNECT_MESSAGE_SOURCE) {
        return;
      }

      if (data.pending) {
        clearConnectPoll();
        void checkSocialConnections();
        return;
      }

      if (data.success === false || data.error) {
        const message =
          (typeof data.message === "string" && data.message) ||
          "Social account connection failed. Please try again.";
        notifyConnectFailed(message);
        clearConnectPoll();
        void checkSocialConnections();
        return;
      }

      const platform = data.success;
      if (platform !== "facebook" && platform !== "instagram") return;

      notifyConnected(platform, typeof data.name === "string" ? data.name : undefined);
      clearConnectPoll();
      void checkSocialConnections();
    };

    window.addEventListener("message", handlePostMessage);

    // When the user comes back from the Facebook popup, re-check straight away
    // instead of waiting for the next poll tick (or a manual page refresh).
    let lastFocusCheck = 0;
    const handleReturnToPage = () => {
      if (document.visibilityState === "hidden") return;
      const now = Date.now();
      if (now - lastFocusCheck < 1500) return;
      lastFocusCheck = now;
      void checkSocialConnections();
    };
    window.addEventListener("focus", handleReturnToPage);
    document.addEventListener("visibilitychange", handleReturnToPage);

    return () => {
      publishPollCancelledRef.current = true;
      window.removeEventListener("message", handlePostMessage);
      window.removeEventListener("focus", handleReturnToPage);
      document.removeEventListener("visibilitychange", handleReturnToPage);
      clearConnectPoll();
      previewGenTokenRef.current += 1;
    };
  }, []);

  const fetchSentAndScheduledCampaigns = async () => {
    if (!getStoredAuthToken()) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await axiosInstance.get(endpoints.listCampaigns);
      const data = Array.isArray(res.data) ? res.data : res.data?.campaigns || [];

      const filtered = data.filter((c: any) =>
        ["sent", "scheduled", "running"].includes(c.status?.toLowerCase())
      );

      const sorted = filtered.sort(
        (a: Campaign, b: Campaign) => getLaunchSortTime(b) - getLaunchSortTime(a)
      );

      setCampaigns(sorted);
    } catch (err) {
      toast.error("Failed to load launches");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openPopupAndSync = (url: string, platform: "facebook" | "instagram") => {
    clearConnectPoll();
    connectToastKeyRef.current = null;

    const popup = window.open(url, "social-popup", "width=600,height=700");

    if (!popup) {
      toast.error("Popup blocked. Please allow popups for this site.");
      return;
    }

    const wasConnected =
      platform === "facebook" ? facebookConnectedRef.current : instagramConnectedRef.current;
    // Used to tell "switched to another page" apart from "nothing changed".
    const previousName = platform === "facebook" ? facebookPageName : instagramPageName;
    const platformLabel = platform === "facebook" ? "Facebook" : "Instagram";
    const nameOf = (status: Awaited<ReturnType<typeof checkSocialConnections>>) =>
      platform === "facebook" ? status.facebookName : status.instagramName;

    // Facebook's login page sets a Cross-Origin-Opener-Policy header, which makes
    // popup.closed report true almost immediately and drops window.opener, so the
    // popup's state is NOT a reliable "finished" signal. Instead keep polling the
    // backend until it reports a result (success, error, page picker) or we time out.
    let attempts = 0;
    const maxAttempts = 100; // ~2.5 minutes
    const pollIntervalMs = 1500;
    let inFlight = false;

    const closePopup = () => {
      try {
        popup.close();
      } catch {
        // Popup may already be closed or cross-origin.
      }
    };

    connectPollRef.current = window.setInterval(async () => {
      if (inFlight) return;
      attempts += 1;
      inFlight = true;

      try {
        // Also handles lastResult / lastError / pendingSelection: each of those
        // shows its toast or dialog and stops this poll.
        const status = await checkSocialConnections();
        if (connectPollRef.current === undefined) {
          closePopup();
          return;
        }

        const nowConnected = platform === "facebook" ? status.facebook : status.instagram;
        const nowName = nameOf(status);

        if (nowConnected && (!wasConnected || nowName !== previousName)) {
          clearConnectPoll();
          notifyConnected(platform, nowName || undefined);
          closePopup();
          return;
        }

        if (attempts >= maxAttempts) {
          clearConnectPoll();
          toast.info(
            wasConnected
              ? `No change detected — ${platformLabel} is still connected${previousName ? ` to ${previousName}` : ""}.`
              : `${platformLabel} was not connected. Please try again.`
          );
        }
      } catch (err) {
        console.error("Polling error:", err);
        if (attempts >= maxAttempts) {
          clearConnectPoll();
          notifyConnectFailed(`Could not confirm your ${platformLabel} connection. Please refresh and check.`);
        }
      } finally {
        inFlight = false;
      }
    }, pollIntervalMs);
  };

  const connectFacebook = () => {
    const token = getStoredAuthToken();
    const url = token
      ? `${endpoints.facebook_connect}?token=${token}`
      : endpoints.facebook_connect;

    openPopupAndSync(url, "facebook");
  };

  const connectInstagram = async (options?: { forceDialog?: boolean }) => {
    // Instagram posts through a Facebook Page's linked Instagram account. If
    // Facebook is already connected, reuse it instead of re-asking on Facebook.
    if (facebookConnectedRef.current && !options?.forceDialog) {
      clearConnectPoll();
      connectToastKeyRef.current = null;
      setConnectingInstagram(true);
      try {
        const res = await axiosInstance.post(endpoints.instagram_from_facebook);
        const handle = res.data?.igUsername ? `@${res.data.igUsername}` : res.data?.name;
        notifyConnected("instagram", handle || undefined);
        await checkSocialConnections();
        return;
      } catch (err: any) {
        const code = err.response?.data?.code;
        if (code === "facebook_token_expired") {
          // The saved Facebook token is no longer valid: Instagram can't be
          // fixed on its own. Say why and send the user to reconnect Facebook.
          toast.error(err.response.data.error);
          setFacebookConnected(false);
          facebookConnectedRef.current = false;
          setFacebookPageName(null);
          return;
        }
        if (err.response?.status === 404 && code === "no_instagram_account") {
          toast.info(
            `${err.response.data.error} Choose a Page that has Instagram linked to continue.`
          );
        }
        // Otherwise fall through to the regular Facebook dialog.
      } finally {
        setConnectingInstagram(false);
      }
    }

    const token = getStoredAuthToken();
    const url = token
      ? `${endpoints.instagram_connect}?token=${token}`
      : endpoints.instagram_connect;

    openPopupAndSync(url, "instagram");
  };

  const confirmPageSelection = async () => {
    if (!pendingSelection || !selectedPageId) return;
    setSavingPage(true);
    try {
      const res = await axiosInstance.post(endpoints.select_social_page, {
        platform: pendingSelection.platform,
        page_id: selectedPageId,
        selection_id: pendingSelection.selectionId,
      });
      notifyConnected(pendingSelection.platform, res.data?.name || undefined);
      setPendingSelection(null);
      await checkSocialConnections();
    } catch (err: any) {
      toast.error(
        err.response?.data?.error || "Could not save the selected page. Please try again."
      );
      if (err.response?.status === 410 || err.response?.status === 409) {
        setPendingSelection(null);
        void checkSocialConnections();
      }
    } finally {
      setSavingPage(false);
    }
  };

  const disconnectPlatform = async (platform: "facebook" | "instagram") => {
    try {
      await axiosInstance.delete(endpoints.disconnect_social(platform));
      if (platform === "facebook") {
        facebookConnectedRef.current = false;
        setFacebookConnected(false);
        setFacebookPageName(null);
      } else {
        instagramConnectedRef.current = false;
        setInstagramConnected(false);
        setInstagramPageName(null);
      }
      toast.success(`Disconnected from ${platform.charAt(0).toUpperCase() + platform.slice(1)}`);
    } catch (err: any) {
      console.error("Disconnect error:", err);
      toast.error(err.response?.data?.error || "Disconnect failed");
    }
  };

  const openUploadDialog = (platform: 'facebook' | 'instagram', campaign: Campaign) => {
    if (platform === "facebook" && !facebookConnected) {
      toast.error("Please connect your Facebook account first.");
      connectFacebook();
      return;
    }
    if (platform === "instagram" && !instagramConnected) {
      toast.error("Please connect your Instagram account first.");
      void connectInstagram();
      return;
    }

    fetchCampaignDetails(campaign.id, platform, campaign);
  };

  const normalizeAssetUrl = (url?: string | null) => {
    const value = (url || "").trim();
    if (!value) return "";
    if (/^(data:|blob:|https?:\/\/)/i.test(value)) return value;
    if (value.startsWith("//")) return `${window.location.protocol}${value}`;
    if (value.startsWith("/")) return `${API_ORIGIN}${value}`;
    return value;
  };

  const getImageUrl = (c: Campaign) =>
    normalizeAssetUrl(c.image_url || c.thumbnail_url || c.campaign_image || "");

  // The flyer is rendered server-side from the exact launch email (headless
  // Chromium) and cached until the email changes; `refresh` forces a re-render.
  const generateImageFromCampaign = async (campaign: Campaign, refresh = false) => {
    const token = ++previewGenTokenRef.current;
    const fallbackUrl = getImageUrl(campaign) || "";

    setLoadingPreview(true);
    setPreviewImageFailed(false);

    try {
      const res = await axiosInstance.post(
        `${endpoints.listCampaigns}${campaign.id}/social-image/`,
        { refresh }
      );
      if (previewGenTokenRef.current !== token) return;
      setGeneratedImageUrl(res.data.url || fallbackUrl || null);
    } catch (err) {
      console.error("Flyer generation failed:", err);
      if (previewGenTokenRef.current === token) {
        setGeneratedImageUrl(fallbackUrl || null);
        if (fallbackUrl) {
          toast.warning("Couldn't generate the flyer image; using the launch image instead");
        } else {
          toast.error("Could not generate preview image");
        }
      }
    } finally {
      if (previewGenTokenRef.current === token) {
        setLoadingPreview(false);
      }
    }
  };

  const fetchCampaignDetails = async (
    campaignId: string,
    platform: "facebook" | "instagram",
    campaignData: Campaign
  ) => {
    previewGenTokenRef.current += 1;
    setGeneratedImageUrl(null);
    setPreviewImageFailed(false);
    setLoadingPreview(true);

    try {
      const res = await axiosInstance.get(`${endpoints.listCampaigns}${campaignId}/`);

      const fullCampaign: Campaign = {
        ...campaignData,
        ...res.data,
        image_url: res.data.image_url || res.data.thumbnail_url || campaignData.image_url,
        thumbnail_url: res.data.thumbnail_url || res.data.image_url || campaignData.thumbnail_url,
        html: res.data.html || campaignData.html,
      };

      setUploadDialog({ isOpen: true, platform, campaign: fullCampaign });
      setCaption(getCampaignTitle(fullCampaign));
      void generateImageFromCampaign(fullCampaign);
    } catch (err) {
      console.error("Failed to load campaign details:", err);
      setUploadDialog({ isOpen: true, platform, campaign: campaignData });
      setCaption(getCampaignTitle(campaignData));
      setGeneratedImageUrl(getImageUrl(campaignData) || null);
      setLoadingPreview(false);
    }
  };

  const closeUploadDialog = () => {
    publishPollCancelledRef.current = true;
    previewGenTokenRef.current += 1;
    setUploadDialog({ isOpen: false, platform: null, campaign: null });
    setCaption("");
    setIsUploading(false);
    setLoadingPreview(false);
    setGeneratedImageUrl(null);
    setPreviewImageFailed(false);
  };

  const handleUploadDialogOpenChange = (open: boolean) => {
    if (!open) closeUploadDialog();
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadDialog.campaign || !uploadDialog.platform) return;

    setIsUploading(true);
    publishPollCancelledRef.current = false;

    try {
      let imageUrl = generatedImageUrl || getImageUrl(uploadDialog.campaign);
      if (!imageUrl) {
        toast.error("No image found for this launch");
        setIsUploading(false);
        return;
      }

      // === DEBUG: Check if token exists ===
      const token =
        sessionStorage.getItem("access_token") ||
        localStorage.getItem("access_token") ||
        sessionStorage.getItem("token") ||
        localStorage.getItem("token");
    
      if (!token) {
        toast.error("Authentication token missing. Please log in again.");
        setIsUploading(false);
        return;
      }

      const endpoint = uploadDialog.platform === "facebook" 
        ? endpoints.facebook_post
        : endpoints.instagram_post;


      const postedCampaignId = uploadDialog.campaign.id;
      const postedPlatform = uploadDialog.platform;
      let response = await axiosInstance.post(endpoint, {
        image_url: imageUrl,
        caption: caption || `Check out this launch!`,
        campaign_id: postedCampaignId,
      });

      // Instagram still processing the image: poll until it is published, fails,
      // or the deadline passes.
      if (response.status === 202 && response.data?.status === "processing") {
        const creationId = response.data.creation_id as string;
        const toastId = toast.loading("Instagram is processing your image…");
        const deadline = Date.now() + INSTAGRAM_POLL_TIMEOUT_MS;
        try {
          while (response.status === 202) {
            if (Date.now() >= deadline) {
              throw new Error(
                "Instagram is taking longer than usual. Please check your Instagram account, or try again in a minute."
              );
            }
            await sleep(INSTAGRAM_POLL_INTERVAL_MS);
            if (publishPollCancelledRef.current) {
              // User left or closed the dialog: abandon quietly. Nothing was
              // published; Instagram expires the unused container on its own.
              return;
            }
            response = await axiosInstance.post(endpoints.instagram_publish, {
              creation_id: creationId,
              campaign_id: postedCampaignId,
            });
          }
        } finally {
          toast.dismiss(toastId);
        }
      }

      if (response?.data?.success || response?.data?.post_id) {
        const postedAt = new Date().toISOString();
        setCampaigns((prev) =>
          prev.map((c) =>
            c.id === postedCampaignId ? { ...c, [POSTED_AT_FIELD[postedPlatform]]: postedAt } : c
          )
        );
        toast.success(`Posted to ${uploadDialog.platform.charAt(0).toUpperCase() + uploadDialog.platform.slice(1)} successfully!`);
        setCaption("");
        closeUploadDialog();
      } else {
        const errorMsg = response?.data?.error || "Post failed";
        toast.error(typeof errorMsg === "string" ? errorMsg : "Failed to post");
      }
    } catch (err: any) {
      console.error("Upload error:", err);
      console.error("Error response:", err.response?.data);
      console.error("Status:", err.response?.status);

      if (err.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        // Optional: redirect to login
        // window.location.href = "/login";
      } else {
        const errorMsg = err.response?.data?.error || err.message || "Post failed";
        toast.error(`Failed to post: ${errorMsg}`);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const getCampaignTitle = (c: Campaign) => {
    if (c.campaign_name?.trim()) return c.campaign_name.trim();
    if (c.campaign_type?.toLowerCase() === "service") return c.service_type || "Service Campaign";
    if (c.property_title?.trim()) return c.property_title.trim();
    return "Untitled Launch";
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "sent": return "bg-green-100 text-green-700 border-green-200";
      case "scheduled": return "bg-blue-100 text-blue-700 border-blue-200";
      case "running": return "bg-purple-100 text-purple-700 border-purple-200";
      default: return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const formatDateUTC = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
      time: date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }) + " UTC",
    };
  };

  const formatPostedAt = (value: string) =>
    new Date(value).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });

  const renderShareButton = (platform: SocialPlatform, campaign: Campaign) => {
    const isFacebook = platform === "facebook";
    const label = isFacebook ? "Facebook" : "Instagram";
    const PlatformIcon = isFacebook ? Facebook : Instagram;
    const connected = isFacebook ? facebookConnected : instagramConnected;
    const postedAt = campaign[POSTED_AT_FIELD[platform]];

    if (postedAt) {
      const title = `Uploaded to ${label} on ${formatPostedAt(postedAt)}.${connected ? " Click to post again." : ""}`;
      return (
        <Button
          size="sm"
          variant="outline"
          disabled={!connected}
          onClick={() => openUploadDialog(platform, campaign)}
          title={title}
          aria-label={title}
          className={cn(
            "min-w-[148px] justify-center gap-1.5 bg-white font-semibold",
            isFacebook
              ? "border-[#1877F2]/40 text-[#1877F2] hover:bg-[#1877F2]/5 hover:text-[#1877F2]"
              : "border-[#DD2A7B]/40 text-[#C13584] hover:bg-[#DD2A7B]/5 hover:text-[#C13584]",
            !connected && "opacity-60 cursor-not-allowed"
          )}
        >
          <PlatformIcon className="h-4 w-4" aria-hidden="true" />
          Uploaded
          <CheckCircle className="h-4 w-4 text-emerald-600" aria-hidden="true" />
        </Button>
      );
    }

    return (
      <Button
        size="sm"
        disabled={!connected}
        onClick={() => openUploadDialog(platform, campaign)}
        className={cn(
          "min-w-[148px] justify-center text-white",
          isFacebook
            ? "bg-[#1877F2] hover:bg-[#166fe5]"
            : "bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:brightness-110",
          !connected && "opacity-50 cursor-not-allowed"
        )}
      >
        {connected ? <CheckCircle className="h-4 w-4 mr-2" /> : <PlatformIcon className="h-4 w-4 mr-2" />}
        {label}
      </Button>
    );
  };

  return (
    <div className="flex min-h-screen bg-white">
      <Sidebar registerMobileToggle={(fn) => { toggleMobileRef.current = fn; }} />

      {/* min-w-0: without it this flex child grows to the table's width and the whole page overflows sideways on small screens */}
      <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
        <PageHeader
          title="Social Media Uploads"
          subtitle="Share your sent & scheduled Launches on Facebook and Instagram"
          onMenuClick={() => toggleMobileRef.current?.()}
        />

        {/* extra bottom padding below md keeps the last row clear of the floating call/chat buttons */}
        <div className="flex-1 p-3 pb-24 sm:p-6 sm:pb-24 md:p-8 overflow-y-auto">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* YOUR EXACT SAME CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Facebook */}
              <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="p-4">
                  {loadingConnections ? (
                    <div className="flex justify-center py-4"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
                  ) : (
                    <div className="flex flex-wrap items-center justify-between gap-3 lg:flex-nowrap lg:gap-0">
                      <div className="flex min-w-0 items-center gap-3 lg:min-w-[auto]">
                        <div className="h-12 w-12 flex-shrink-0 lg:flex-shrink rounded-xl bg-[#1877F2] flex items-center justify-center shadow-md">
                          <Facebook className="h-6 w-6 text-white" />
                        </div>
                        <div className="min-w-0 lg:min-w-[auto]">
                          <p className="font-bold text-gray-900 text-lg">Facebook</p>
                          {facebookConnected ? (
                            <p className="text-xs text-green-600 flex items-center gap-1 font-medium">
                              <CheckCircle className="h-3.5 w-3.5" /> Connected
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500 flex items-center gap-1">
                              <XCircle className="h-3.5 w-3.5" /> Not connected
                            </p>
                          )}
                          {facebookConnected && facebookPageName && (
                            <p
                              className="mt-0.5 text-sm font-medium text-gray-800 truncate max-w-[180px] sm:max-w-[240px]"
                              title={facebookPageName}
                            >
                              <span className="text-gray-500 font-normal">Page: </span>
                              {facebookPageName}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 lg:flex-nowrap">
                        {facebookConnected && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={connectFacebook}
                            title="Connect a different Facebook Page"
                          >
                            Switch page
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={facebookConnected ? () => disconnectPlatform("facebook") : connectFacebook}
                          className={cn(
                            "gap-2",
                            facebookConnected
                              ? "bg-gray-200 text-gray-700 hover:bg-gray-300"
                              : "bg-[#1877F2] hover:bg-[#166fe5] text-white"
                          )}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          {facebookConnected ? "Disconnect" : "Connect"}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Instagram */}
              <Card className="border-2 border-pink-200 bg-gradient-to-br from-pink-50 to-white">
                <CardContent className="p-4">
                  {loadingConnections ? (
                    <div className="flex justify-center py-4"><Loader2 className="h-6 w-6 animate-spin text-pink-500" /></div>
                  ) : (
                    <div className="flex flex-wrap items-center justify-between gap-3 lg:flex-nowrap lg:gap-0">
                      <div className="flex min-w-0 items-center gap-3 lg:min-w-[auto]">
                        <div className="h-12 w-12 flex-shrink-0 lg:flex-shrink rounded-xl bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] flex items-center justify-center shadow-md">
                          <Instagram className="h-6 w-6 text-white" />
                        </div>
                        <div className="min-w-0 lg:min-w-[auto]">
                          <p className="font-bold text-gray-900 text-lg">Instagram</p>
                          {instagramConnected ? (
                            <p className="text-xs text-green-600 flex items-center gap-1 font-medium">
                              <CheckCircle className="h-3.5 w-3.5" /> Connected
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500 flex items-center gap-1">
                              <XCircle className="h-3.5 w-3.5" /> Not connected
                            </p>
                          )}
                          {instagramConnected && instagramPageName && (
                            <p
                              className="mt-0.5 text-sm font-medium text-gray-800 truncate max-w-[180px] sm:max-w-[240px]"
                              title={instagramPageName}
                            >
                              <span className="text-gray-500 font-normal">Page: </span>
                              {instagramPageName}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 lg:flex-nowrap">
                        {instagramConnected && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void connectInstagram({ forceDialog: true })}
                            title="Choose a different Instagram account"
                          >
                            Switch page
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={instagramConnected ? () => disconnectPlatform("instagram") : () => void connectInstagram()}
                          disabled={connectingInstagram}
                          className={cn(
                            "gap-2",
                            instagramConnected
                              ? "bg-gray-200 text-gray-700 hover:bg-gray-300"
                              : "bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:brightness-110 text-white"
                          )}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          {instagramConnected ? "Disconnect" : "Connect"}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Main Campaign Table */}
            <Card className="shadow-lg border-0 overflow-hidden">
              <CardHeader className="bg-white border-b">
                <div className="flex items-center justify-between">
                  <div className="flex min-w-0 items-center gap-3 lg:min-w-[auto]">
                    <div className="h-10 w-10 flex-shrink-0 lg:flex-shrink rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                      <Share2 className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-gray-900">Launch Library</CardTitle>
                      <p className="text-sm text-gray-500 mt-1">
                        {campaigns.length} launch{campaigns.length !== 1 ? "s" : ""} available for sharing
                      </p>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-0">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-16 px-4">
                    <Loader2 className="h-12 w-12 animate-spin text-blue-500 mb-4" />
                    <p className="text-gray-600 font-medium">Loading Launches...</p>
                  </div>
                ) : campaigns.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 px-4">
                    <div className="h-20 w-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                      <ImageIcon className="h-10 w-10 text-gray-400" />
                    </div>
                    <p className="text-gray-900 font-semibold text-lg">No launches found</p>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="hidden md:block overflow-x-auto max-h-[600px]">
                      <Table>
                        <TableHeader className="sticky top-0 bg-gray-50/95 backdrop-blur-sm shadow-sm">
                          <TableRow>
                            <TableHead className="font-semibold text-gray-700 bg-gray-50">Launch</TableHead>
                            <TableHead className="font-semibold text-gray-700 bg-gray-50">Preview</TableHead>
                            <TableHead className="font-semibold text-gray-700 bg-gray-50">Date & Time (UTC)</TableHead>
                            {/* <TableHead className="font-semibold text-gray-700 bg-gray-50">Status</TableHead> */}
                            <TableHead className="text-center font-semibold text-gray-700 bg-gray-50">Share on Social</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {campaigns.map((campaign) => {
                            const id = campaign.id || campaign.property_id;
                            const imageUrl = getImageUrl(campaign);
                            const hasImage = !!imageUrl;

                        
                            const dateTime = campaign.scheduled_date || campaign.date
                              ? formatDateUTC(campaign.scheduled_date || campaign.date)
                              : null;

                            return (
                              <TableRow key={campaign.id}>
                                <TableCell className="font-medium max-w-xs">
                                  <p className="font-semibold text-gray-900 line-clamp-1">
                                    {getCampaignTitle(campaign)}
                                  </p>
                                </TableCell>

                                <TableCell>
                                  {hasImage ? (
                                    <img
                                      src={imageUrl}
                                      alt={getCampaignTitle(campaign)}
                                      className="w-24 h-24 object-cover rounded-lg border-2 border-gray-200 shadow-sm"
                                      onError={(e) => (e.currentTarget.src = "/placeholder.svg")}
                                    />
                                  ) : (
                                    <div className="bg-gray-100 border-2 border-dashed rounded-lg w-24 h-24 flex items-center justify-center">
                                      <ImageIcon className="h-6 w-6 text-gray-400" />
                                    </div>
                                  )}
                                </TableCell>

                                <TableCell className="text-sm">
                                  {dateTime ? (
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2">
                                        <Calendar className="h-4 w-4 text-blue-500" />
                                        <p className="font-medium text-gray-900">{dateTime.date}</p>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Clock className="h-4 w-4 text-purple-500" />
                                        <p className="text-xs text-gray-600 font-mono">{dateTime.time}</p>
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </TableCell>

                                {/* <TableCell>
                                  <Badge variant="outline" className={`${getStatusColor(campaign.status)} font-semibold capitalize px-3 py-1`}>
                                    {campaign.status}
                                  </Badge>
                                </TableCell> */}

                                <TableCell>
                                  <div className="flex items-center gap-3 justify-center">
                                    {renderShareButton("facebook", campaign)}
                                    {renderShareButton("instagram", campaign)}
                                  </div>
                                </TableCell> 
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Phones: one card per launch instead of a 4-column table (md+ keeps the table above) */}
                    <div className="md:hidden max-h-[600px] overflow-y-auto divide-y divide-gray-100">
                      {campaigns.map((campaign) => {
                        const imageUrl = getImageUrl(campaign);
                        const dateTime = campaign.scheduled_date || campaign.date
                          ? formatDateUTC(campaign.scheduled_date || campaign.date)
                          : null;

                        return (
                          <div key={`mobile-${campaign.id}`} className="space-y-3 p-4">
                            <div className="flex items-start gap-3">
                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt={getCampaignTitle(campaign)}
                                  className="h-20 w-20 flex-shrink-0 rounded-lg border-2 border-gray-200 object-cover shadow-sm"
                                  onError={(e) => (e.currentTarget.src = "/placeholder.svg")}
                                />
                              ) : (
                                <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-lg border-2 border-dashed bg-gray-100">
                                  <ImageIcon className="h-6 w-6 text-gray-400" />
                                </div>
                              )}

                              <div className="min-w-0 flex-1 space-y-1.5">
                                <p className="break-words font-semibold text-gray-900 line-clamp-2">
                                  {getCampaignTitle(campaign)}
                                </p>
                                {dateTime ? (
                                  <>
                                    <div className="flex items-center gap-2">
                                      <Calendar className="h-4 w-4 flex-shrink-0 text-blue-500" />
                                      <p className="text-sm font-medium text-gray-900">{dateTime.date}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Clock className="h-4 w-4 flex-shrink-0 text-purple-500" />
                                      <p className="font-mono text-xs text-gray-600">{dateTime.time}</p>
                                    </div>
                                  </>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </div>
                            </div>

                            {/* share buttons keep their own min width; they wrap onto two rows on very narrow phones */}
                            <div className="flex flex-wrap gap-2 [&>button]:flex-1">
                              {renderShareButton("facebook", campaign)}
                              {renderShareButton("instagram", campaign)}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {campaigns.length > 5 && (
                      <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none flex items-end justify-center pb-2">
                        <div className="bg-gray-100 rounded-full px-3 py-1 text-xs text-gray-500 font-medium shadow-sm">
                          Scroll for more launches
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        <CallingBot/>
        <Chatbot />
      </main>

      {/* Page picker: shown when the Facebook account grants more than one Page */}
      <Dialog
        open={!!pendingSelection}
        onOpenChange={(open) => {
          if (!open && !savingPage) setPendingSelection(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {pendingSelection?.platform === "instagram"
                ? "Choose an Instagram account"
                : "Choose a Facebook Page"}
            </DialogTitle>
            <DialogDescription>
              {pendingSelection?.platform === "instagram"
                ? "These Instagram business accounts are linked to your Facebook Pages. Launches will be posted to the one you pick."
                : "Your launches will be posted to the Page you pick. You can switch any time."}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-80 overflow-y-auto space-y-2 py-1" role="radiogroup">
            {pendingSelection?.pages.map((page) => {
              const active = page.id === selectedPageId;
              return (
                <button
                  key={page.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setSelectedPageId(page.id)}
                  className={cn(
                    "w-full flex items-center gap-3 rounded-lg border-2 p-3 text-left transition-colors",
                    active ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"
                  )}
                >
                  {page.picture ? (
                    <img src={page.picture} alt="" className="h-10 w-10 rounded-full object-cover" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                      <Facebook className="h-5 w-5 text-gray-400" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-900 truncate">
                      {pendingSelection?.platform === "instagram" && page.igUsername
                        ? `@${page.igUsername}`
                        : page.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {pendingSelection?.platform === "instagram"
                        ? `via Facebook Page: ${page.name}`
                        : page.category || "Facebook Page"}
                    </p>
                  </div>
                  {active && <CheckCircle className="h-5 w-5 text-blue-600 shrink-0" />}
                </button>
              );
            })}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPendingSelection(null)} disabled={savingPage}>
              Cancel
            </Button>
            <Button onClick={confirmPageSelection} disabled={!selectedPageId || savingPage}>
              {savingPage && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Connect this page
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={uploadDialog.isOpen} onOpenChange={handleUploadDialogOpenChange}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              Upload to {uploadDialog.platform === 'facebook' ? 'Facebook' : 'Instagram'}
            </DialogTitle>
            <DialogDescription>Review your launch and customize the post</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleUpload} className="space-y-4 py-4">
            {uploadDialog.campaign && (
              <>
                {/* Campaign Info */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-2">
                  <p className="text-xs text-gray-500 font-semibold uppercase">Launch Details</p>
                  <p className="font-semibold text-sm text-gray-900">{getCampaignTitle(uploadDialog.campaign)}</p>
                  {uploadDialog.campaign.campaign_subject && (
                    <p className="text-sm text-gray-700">Subject: {uploadDialog.campaign.campaign_subject}</p>
                  )}
                  {uploadDialog.campaign.scheduled_date && (
                    <p className="text-xs text-gray-600">
                      Scheduled: {new Date(uploadDialog.campaign.scheduled_date).toLocaleString()}
                    </p>
                  )}
                </div>

                {/* Post preview: the exact image that will be posted */}
                {loadingPreview ? (
                  <div className="rounded-lg border-2 border-dashed border-blue-200 bg-blue-50/50 py-10 flex flex-col items-center justify-center gap-2 text-center">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                    <p className="text-sm text-gray-600">Preparing post image…</p>
                    <p className="text-xs text-gray-500">The first preview of a launch can take a few seconds.</p>
                  </div>
                ) : generatedImageUrl && !previewImageFailed ? (
                  <div className="rounded-lg border-2 border-blue-300 overflow-hidden bg-gray-100 shadow-md">
                    <div className="flex items-center justify-between gap-2 p-2 bg-blue-50">
                      <p className="text-xs text-gray-600 font-semibold">Post Preview Image</p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => uploadDialog.campaign && void generateImageFromCampaign(uploadDialog.campaign, true)}
                      >
                        Regenerate
                      </Button>
                    </div>
                    <img
                      src={generatedImageUrl}
                      alt="Post preview"
                      className="block w-full h-auto max-h-[45vh] sm:max-h-[520px] object-contain"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        setPreviewImageFailed(true);
                      }}
                    />
                  </div>
                ) : previewImageFailed ? (
                  <div className="rounded-lg border-2 border-dashed border-red-200 bg-red-50/50 py-8 px-4 flex flex-col items-center justify-center gap-2 text-center">
                    <ImageIcon className="h-8 w-8 text-red-400" />
                    <p className="text-sm font-medium text-red-700">Preview image could not be loaded</p>
                    <p className="text-xs text-red-600">
                      The flyer image couldn't be generated for this launch.
                    </p>
                  </div>
                ) : (
                  <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 py-8 flex flex-col items-center justify-center gap-2">
                    <ImageIcon className="h-8 w-8 text-gray-400" />
                    <p className="text-sm text-gray-500">No preview image available for this launch</p>
                  </div>
                )}

                {/* Campaign Message/Description */}
                {uploadDialog.campaign.message && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-gray-600 mb-2 font-semibold">Launch Description</p>
                    <p className="text-sm text-gray-800">{uploadDialog.campaign.message}</p>
                  </div>
                )}

                {/* Caption */}
                <div className="space-y-2">
                  <Label htmlFor="caption" className="font-semibold">Post Caption</Label>
                  <Textarea
                    id="caption"
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder="Enter your post caption..."
                    rows={3}
                    className="resize-none"
                    required
                  />
                  <p className="text-xs text-gray-500">{caption.length} characters</p>
                </div>
              </>
            )}

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={closeUploadDialog} disabled={isUploading}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isUploading || loadingPreview} 
                className={cn(
                  "gap-2",
                  uploadDialog.platform === 'facebook' 
                    ? 'bg-[#1877F2] hover:bg-[#0F5AD6]' 
                    : 'bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:brightness-110'
                )}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : loadingPreview ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading Preview...
                  </>
                ) : (
                  <>
                    {uploadDialog.platform === 'facebook' ? <Facebook className="h-4 w-4" /> : <Instagram className="h-4 w-4" />}
                    Upload to {uploadDialog.platform === 'facebook' ? 'Facebook' : 'Instagram'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>


        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SocialMediaUploads;
